import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../auth/AuthProvider';
import { useOnTimeLiveData } from '../hooks/useOnTimeLiveData';
import { loadOnTimeSettings } from '../lib/ontimeSettings';
import {
  driverAcceptRun,
  driverOnRoute,
  driverPickedUp,
  markDeliveredWithProof,
} from '../lib/actions';
import { asDate } from '../lib/formatters';

const actionClass = 'rounded-2xl border px-3 py-3 text-sm font-semibold transition';
const inputClass = 'w-full rounded-2xl border border-white/10 bg-[#0b1727] px-4 py-3 text-sm text-white outline-none placeholder:text-slate-500';

function minutesBetween(a, b) {
  const start = asDate(a);
  const end = asDate(b);
  if (!start || !end) return null;
  const diff = Math.round((end.getTime() - start.getTime()) / 60000);
  return Number.isFinite(diff) && diff >= 0 ? diff : null;
}

function formatAvg(minutes) {
  if (!Number.isFinite(minutes)) return '—';
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${h}h ${m}m`;
}

function isSameDay(date, selected) {
  if (!date || !selected) return false;
  const d = asDate(date);
  if (!d) return false;
  return d.toISOString().slice(0, 10) === selected;
}

function orderDeliveredAt(order = {}) {
  return order.raw?.proof?.deliveredAt || order.raw?.timings?.deliveredAt || order.proof?.deliveredAt || order.deliveredAt || order.updatedAt;
}

function orderAcceptedAt(order = {}) {
  const t = order.raw?.timings || order.timings || {};
  return t.driverAcceptedAt || t.driverAssignedAt || t.readyForDriverAt || t.collectedAt || t.outForDeliveryAt || order.createdAt;
}

function orderDriverName(order = {}) {
  return String(order.assignedDriver || order.assignedDriverName || order.raw?.assignment?.driverName || order.raw?.proof?.deliveredBy || '').trim();
}

function driverLabel(driver = {}) {
  return String(driver.name || driver.fullName || driver.displayName || driver.id || 'Driver').trim();
}

function driverMatches(driver = {}, order = {}) {
  const driverValues = [driver.id, driver.name, driver.fullName, driver.displayName, driver.email].map((v) => String(v || '').trim().toLowerCase()).filter(Boolean);
  const orderValues = [orderDriverName(order), order.assignedDriver, order.assignedDriverName, order.driverId, order.raw?.assignment?.driverId, order.raw?.assignment?.driverName, order.raw?.proof?.deliveredBy].map((v) => String(v || '').trim().toLowerCase()).filter(Boolean);
  return driverValues.some((value) => orderValues.includes(value));
}

function buildDriverScore(driver = {}, orders = [], selectedDate = '') {
  const related = orders.filter((order) => driverMatches(driver, order));
  const delivered = related.filter((order) => order.stage === 'delivered');
  const deliveredInPeriod = delivered.filter((order) => isSameDay(orderDeliveredAt(order), selectedDate));
  const active = related.filter((order) => ['ready_for_driver', 'out_for_delivery'].includes(order.stage));
  const cancelled = related.filter((order) => String(order.stage || order.status || '').toLowerCase().includes('cancel'));
  const missed = related.filter((order) => String(order.driverDecision || order.raw?.driverDecision || '').toLowerCase().includes('miss'));
  const durations = deliveredInPeriod.map((order) => minutesBetween(orderAcceptedAt(order), orderDeliveredAt(order))).filter((value) => Number.isFinite(value));
  const avg = durations.length ? Math.round(durations.reduce((sum, value) => sum + value, 0) / durations.length) : null;
  const opportunities = deliveredInPeriod.length + cancelled.length + missed.length;
  const acceptance = opportunities ? Math.round((deliveredInPeriod.length / opportunities) * 100) : (active.length ? 100 : null);
  const reliability = opportunities ? Math.max(0, Math.min(100, Math.round((deliveredInPeriod.length / opportunities) * 100))) : (active.length ? 80 : 100);
  return { related, deliveredInPeriod, active, cancelled, missed, avg, acceptance, reliability };
}

export default function OnTimeDriverPage() {
  const { claims, user } = useAuth();
  const { orders, drivers, usingMock } = useOnTimeLiveData();
  const [settings, setSettings] = useState(null);
  const [receiverName, setReceiverName] = useState('');
  const [note, setNote] = useState('');
  const [pendingOrderId, setPendingOrderId] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().slice(0, 10));
  const [statusMessage, setStatusMessage] = useState('');
  const [statusError, setStatusError] = useState('');
  const fileRef = useRef(null);

  useEffect(() => {
    if (!claims?.orgId) return;
    loadOnTimeSettings(claims.orgId).then(setSettings).catch(() => setSettings(null));
  }, [claims?.orgId]);

  const driverOrders = useMemo(
    () => orders.filter((order) => ['ready_for_driver', 'out_for_delivery'].includes(order.stage)),
    [orders]
  );

  const actor = {
    userId: user?.uid || '',
    name: user?.displayName || user?.email || 'Driver user',
    role: claims?.role || 'driver',
  };

  function resolveDriver(order) {
    return (
      drivers.find((driver) => (driver.name || driver.fullName || driver.id) === order.assignedDriver) ||
      drivers[0] ||
      null
    );
  }

  const selectedDeliveries = useMemo(() => {
    return orders.filter((order) => {
      const deliveredAt = order.raw?.proof?.deliveredAt || order.raw?.timings?.deliveredAt || order.proof?.deliveredAt;
      return order.stage === 'delivered' && isSameDay(deliveredAt || order.raw?.updatedAt || order.createdAt, selectedDate);
    });
  }, [orders, selectedDate]);

  const avgDeliveryMinutes = useMemo(() => {
    const rows = selectedDeliveries
      .map((order) => minutesBetween(order.raw?.placedAt || order.raw?.createdAt || order.createdAt, order.raw?.proof?.deliveredAt || order.raw?.timings?.deliveredAt || order.proof?.deliveredAt))
      .filter((value) => Number.isFinite(value));
    if (!rows.length) return null;
    return Math.round(rows.reduce((sum, value) => sum + value, 0) / rows.length);
  }, [selectedDeliveries]);

  const byDriver = useMemo(() => {
    const map = {};
    selectedDeliveries.forEach((order) => {
      const key = order.assignedDriver || order.raw?.proof?.deliveredBy || 'Unassigned';
      const duration = minutesBetween(order.raw?.placedAt || order.raw?.createdAt || order.createdAt, order.raw?.proof?.deliveredAt || order.raw?.timings?.deliveredAt || order.proof?.deliveredAt);
      if (!map[key]) map[key] = { completed: 0, totalMinutes: 0, measured: 0 };
      map[key].completed += 1;
      if (Number.isFinite(duration)) {
        map[key].totalMinutes += duration;
        map[key].measured += 1;
      }
    });
    return map;
  }, [selectedDeliveries]);


  const driverScoreRows = useMemo(() => {
    return drivers
      .map((driver) => ({ driver, score: buildDriverScore(driver, orders, selectedDate) }))
      .sort((a, b) => (b.score.reliability || 0) - (a.score.reliability || 0));
  }, [drivers, orders, selectedDate]);

  async function run(action, order) {
    if (!claims?.orgId || usingMock) return;
    setStatusMessage('');
    setStatusError('');
    try {
      const selectedDriver = resolveDriver(order);
      const driverId = selectedDriver?.id || `driver-${String(order.assignedDriver || '').toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
      if (action === 'accept') await driverAcceptRun({ orgId: claims.orgId, orderId: order.id, driverId, actor });
      if (action === 'pickup') await driverPickedUp({ orgId: claims.orgId, orderId: order.id, driverId, actor });
      if (action === 'route') await driverOnRoute({ orgId: claims.orgId, orderId: order.id, driverId, actor });
      setStatusMessage(`Driver action saved on ${order.orderNumber || order.id}.`);
    } catch (error) {
      setStatusError(error?.message || 'Driver action failed.');
    }
  }

  function openDeliveredFlow(order) {
    if (!claims?.orgId || usingMock) return;
    setPendingOrderId(order.id);
    fileRef.current?.click();
  }

  async function onProofSelected(event) {
    const file = event.target.files?.[0];
    const order = driverOrders.find((row) => row.id === pendingOrderId);
    if (!file || !order || !claims?.orgId || usingMock) return;

    setStatusMessage('');
    setStatusError('');

    try {
      const selectedDriver = resolveDriver(order);
      await markDeliveredWithProof({
        file,
        orgId: claims.orgId,
        orderId: order.id,
        driverId: selectedDriver?.id || '',
        driverName: selectedDriver?.name || selectedDriver?.fullName || actor.name,
        receiverName,
        note,
        actor,
      });
      setReceiverName('');
      setNote('');
      setPendingOrderId('');
      setStatusMessage('Proof uploaded. Delivery stored on the order history and ready for reports.');
    } catch (error) {
      setStatusError(error?.message || 'Proof upload failed.');
    } finally {
      event.target.value = '';
    }
  }

  const deliveryTarget = settings?.deliveryTargetMinutes || 60;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-white">Driver App Mode</h2>
        <p className="mt-1 text-sm text-slate-400">Mobile-first run sheet for pickups, routes and delivery proof.</p>
      </div>

      {statusMessage ? <div className="rounded-2xl border border-emerald-400/20 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-200">{statusMessage}</div> : null}
      {statusError ? <div className="rounded-2xl border border-rose-400/20 bg-rose-500/10 px-4 py-3 text-sm text-rose-200">{statusError}</div> : null}

      {usingMock ? (
        <div className="rounded-2xl border border-amber-400/20 bg-amber-500/10 px-4 py-3 text-sm text-amber-200">
          Delivery proof is disabled while mock data is active. Add live Link driver and order docs to test uploads.
        </div>
      ) : null}

      <div className="rounded-3xl border border-cyan-400/20 bg-cyan-500/10 p-5 shadow-[0_0_40px_rgba(34,211,238,0.08)]">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.24em] text-cyan-300">Driver intelligence</div>
            <h3 className="mt-2 text-2xl font-semibold text-white">Reliability and delivery quality</h3>
            <p className="mt-1 text-sm text-slate-400">Scores are built from active runs, completed deliveries, missed runs, cancellations, and measured delivery time.</p>
          </div>
          <div className="rounded-2xl border border-white/10 bg-[#081423] px-4 py-3 text-right">
            <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Drivers tracked</div>
            <div className="mt-1 text-2xl font-semibold text-white">{driverScoreRows.length}</div>
          </div>
        </div>
        <div className="mt-5 grid gap-3 lg:grid-cols-3">
          {driverScoreRows.slice(0, 6).map(({ driver, score }) => (
            <div key={driver.id || driverLabel(driver)} className="rounded-2xl border border-white/10 bg-[#081423] p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-semibold text-white">{driverLabel(driver)}</div>
                  <div className="text-sm text-slate-400">{driver.store || driver.storeId || driver.site || 'All assigned sites'}</div>
                </div>
                <div className={score.reliability >= 80 ? 'text-emerald-300' : score.reliability >= 60 ? 'text-amber-300' : 'text-rose-300'}>{score.reliability}%</div>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
                <div className="rounded-xl bg-white/5 p-3"><span className="text-slate-500">Active</span><div className="mt-1 text-white">{score.active.length}</div></div>
                <div className="rounded-xl bg-white/5 p-3"><span className="text-slate-500">Completed</span><div className="mt-1 text-white">{score.deliveredInPeriod.length}</div></div>
                <div className="rounded-xl bg-white/5 p-3"><span className="text-slate-500">Accept rate</span><div className="mt-1 text-white">{Number.isFinite(score.acceptance) ? `${score.acceptance}%` : '—'}</div></div>
                <div className="rounded-xl bg-white/5 p-3"><span className="text-slate-500">Avg run</span><div className="mt-1 text-white">{formatAvg(score.avg)}</div></div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid gap-4 xl:grid-cols-4">
        <div className="rounded-3xl border border-white/10 bg-white/5 p-5">
          <div className="text-xs uppercase tracking-[0.22em] text-slate-500">Delivery target</div>
          <div className="mt-3 text-3xl font-semibold text-white">{deliveryTarget} min</div>
          <div className="mt-2 text-sm text-slate-400">Pulled from Link settings.</div>
        </div>
        <div className="rounded-3xl border border-white/10 bg-white/5 p-5">
          <div className="text-xs uppercase tracking-[0.22em] text-slate-500">Avg completion</div>
          <div className="mt-3 text-3xl font-semibold text-white">{formatAvg(avgDeliveryMinutes)}</div>
          <div className="mt-2 text-sm text-slate-400">For {selectedDate} across delivered orders.</div>
        </div>
        <div className="rounded-3xl border border-white/10 bg-white/5 p-5">
          <div className="text-xs uppercase tracking-[0.22em] text-slate-500">Completed</div>
          <div className="mt-3 text-3xl font-semibold text-white">{selectedDeliveries.length}</div>
          <div className="mt-2 text-sm text-slate-400">Orders with delivered proof / timestamp.</div>
        </div>
        <div className="rounded-3xl border border-white/10 bg-white/5 p-5">
          <div className="text-xs uppercase tracking-[0.22em] text-slate-500">Date picker</div>
          <input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} className={`${inputClass} mt-3`} />
          <div className="mt-2 text-sm text-slate-400">Use custom dates for daily driver checks.</div>
        </div>
      </div>

      <div className="rounded-3xl border border-white/10 bg-white/5 p-5">
        <div className="text-sm font-medium uppercase tracking-[0.18em] text-slate-500">Delivery confirmation</div>
        <div className="mt-4 grid gap-3 md:grid-cols-2">
          <input
            value={receiverName}
            onChange={(e) => setReceiverName(e.target.value)}
            placeholder="Receiver name (optional)"
            className={inputClass}
          />
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Delivery note (optional)"
            className={inputClass}
          />
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-[290px_minmax(0,1fr)]">
        <aside className="rounded-3xl border border-white/10 bg-white/5 p-5">
          <div className="text-lg font-semibold text-white">Drivers</div>
          <div className="mt-4 space-y-3">
            {drivers.map((driver) => {
              const perf = byDriver[driver.name || driver.fullName || driver.id] || { completed: 0, totalMinutes: 0, measured: 0 };
              const avg = perf.measured ? Math.round(perf.totalMinutes / perf.measured) : null;
              const hot = Number.isFinite(avg) && avg > deliveryTarget;
              return (
                <div key={driver.id} className="rounded-2xl bg-[#0b1727] p-4 text-sm">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="font-semibold text-white">{driver.name || driver.fullName || driver.id}</div>
                      <div className="text-slate-400">{driver.store || driver.storeId || 'Unassigned store'}</div>
                    </div>
                    <div className="text-cyan-300">{driver.load || 0} active</div>
                  </div>
                  <div className="mt-2 text-slate-500">Status: {driver.status || 'available'}</div>
                  <div className="mt-2 flex items-center justify-between text-xs">
                    <span className="text-slate-500">Avg today</span>
                    <span className={hot ? 'text-amber-300' : 'text-emerald-300'}>{formatAvg(avg)}</span>
                  </div>
                  <div className="mt-1 flex items-center justify-between text-xs">
                    <span className="text-slate-500">Completed</span>
                    <span className="text-white">{perf.completed}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </aside>

        <section className="space-y-4">
          {driverOrders.map((order) => (
            <div key={order.id} className="rounded-3xl border border-white/10 bg-white/5 p-5">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="text-sm text-slate-400">{order.orderNumber || order.id}</div>
                  <h3 className="text-lg font-semibold text-white">{order.customerName}</h3>
                  <div className="text-slate-500">{order.address}</div>
                </div>
                <div className="text-right text-sm">
                  <div className="text-white">{order.assignedDriver || 'Unassigned'}</div>
                  <div className="text-slate-400">{String(order.paymentMethod || '').replaceAll('_', ' ')}</div>
                </div>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-2 md:grid-cols-4">
                <button onClick={() => run('accept', order)} className={`${actionClass} border-white/10 bg-[#0b1727] text-white`}>Accept Run</button>
                <button onClick={() => run('pickup', order)} className={`${actionClass} border-sky-400/30 bg-sky-500/10 text-sky-200`}>Picked Up</button>
                <button onClick={() => run('route', order)} className={`${actionClass} border-violet-400/30 bg-violet-500/10 text-violet-200`}>Out for Delivery</button>
                <button onClick={() => openDeliveredFlow(order)} className={`${actionClass} border-emerald-400/30 bg-emerald-500/10 text-emerald-200`}>Delivered + Proof</button>
              </div>
            </div>
          ))}
        </section>
      </div>

      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={onProofSelected}
      />
    </div>
  );
}
